import { ButtonProps } from '@mui/material'
import { RuuviTag } from '../types'
import { detachRuuviTag } from '../services/ruuviTagService'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { LoadingButton } from '@mui/lab'

export interface RuuviTagDetachButtonProps extends Omit<ButtonProps, 'children' | 'onClick'> {
  ruuviTag: RuuviTag
  buttonText?: string
  beforeSubmit?: () => void
  afterSubmit?: (ruuviTag: RuuviTag) => void
  onError?: (error: unknown) => void
}

export default function RuuviTagDetachButton({
  ruuviTag,
  buttonText,
  beforeSubmit,
  afterSubmit,
  onError,
  ...props
}: RuuviTagDetachButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('ruuvitag')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The submitting state.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  /**
   * Handle the "submit" action.
   */
  async function handleSubmit() {
    try {
      if (ruuviTag?.asset) {
        setSubmitting(true)

        beforeSubmit?.()

        const updatedRuuviTag = await detachRuuviTag(ruuviTag)

        dispatch(createSuccessNotification(t('detachRuuviTagSuccess')))

        afterSubmit?.(updatedRuuviTag)
      }
    } catch (error) {
      dispatch(createErrorNotification(t('detachRuuviTagError')))
      onError?.(error)
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false)
      }
    }
  }

  return (
    <>
      {ruuviTag?.asset && (
        <LoadingButton
          variant="contained"
          color="warning"
          loading={isSubmitting}
          {...props}
          onClick={handleSubmit}
        >
          {buttonText ?? t('detachFromProject')}
        </LoadingButton>
      )}
    </>
  )
}
